// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
/* eslint-disable-next-line no-unused-vars */
import React, { type Node } from 'react';

import {
  Animation,
  type ColorName,
  Colors,
  type ShadowName,
  Shadows,
} from '@zapier/design-system';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

/** @jsx isolateComponent */

type Props = {
  /** The color of the surface's background. */
  backgroundColor: ColorName,
  /** The color of border to render around the surface. */
  borderColor?: ColorName,
  /** Child node to render inside the surface. */
  children: Node,
  /** Applies a `box-shadow` with the specified shadow. */
  shadow?: ShadowName,
};

const Styles = labelStyles(__filename, {
  root: (props: Props) => [
    {
      borderRadius: 10,
      transitionProperty: 'box-shadow, border-color, background-color',
      transitionTimingFunction: Animation.transitionTimingFunction,
      transitionDuration: Animation.transitionDuration,
      alignItems: 'stretch',
      flex: 1,
      maxWidth: '100%',
    },

    props.backgroundColor &&
      withModifier(props.backgroundColor, {
        backgroundColor: Colors[props.backgroundColor],
      }),

    props.borderColor &&
      withModifier(`border-${props.borderColor}`, {
        border: `1px solid ${Colors[props.borderColor]}`,
      }),

    props.shadow &&
      withModifier('hasShadow', {
        boxShadow: Shadows[props.shadow],
      }),
  ],
});

/**
 * Renders a Surface component. Surfaces represent content blocks, they are useful
 * for creating visual emphasis of content on a page. <br />
 * The Surface component acts as a "wrapping" component
 *
 * Tags: core-ui
 */
const Surface = (props: Props) => {
  return <div css={Styles.root(props)}>{props.children}</div>;
};

Surface.defaultProps = {
  backgroundColor: 'transparent',
  shadow: 'elevation0',
};

export default Surface;
